import { useEffect, useState } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@xiltrion/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/client';
import { searchCitizens } from '@/features/citizens/api';
import type { CaseRow, Page } from '@/api/types';
import { StatusTag } from '@/components/ui';

/** Global search: grouped results. Cases use PostgreSQL full-text; citizens via /citizens?q. */
export function GlobalSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const nav = useNavigate();
  useEffect(() => { if (!open) setQ(''); }, [open]);
  const ready = open && q.trim().length >= 2;
  const cases = useQuery({ queryKey: ['search', 'cases', q], queryFn: () => apiGet<Page<CaseRow>>('/cases', { q, limit: 8 }), enabled: ready });
  const citizens = useQuery({ queryKey: ['search', 'citizens', q], queryFn: () => searchCitizens(q), enabled: ready });
  const go = (to: string) => { onClose(); nav(to); };
  const loading = cases.isLoading || citizens.isLoading;
  const caseItems = cases.data?.items ?? [];
  const people = (citizens.data ?? []).slice(0, 5);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[640px]">
        <DialogHeader className="sr-only"><DialogTitle>Buscar</DialogTitle><DialogDescription>Busca expedientes y personas</DialogDescription></DialogHeader>
        {/* Los resultados ya vienen filtrados del API: cmdk no vuelve a filtrar. */}
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder="Radicado, ciudadano, asunto, documento…" aria-label="Buscar" />
          <CommandList className="max-h-[420px]">
            {!ready ? <div className="px-3 py-6 text-center text-sm text-muted-foreground">Escribe al menos 2 caracteres.</div>
              : loading ? <div className="px-3 py-6 text-center text-sm text-muted-foreground">Buscando…</div>
              : <CommandEmpty>Sin resultados</CommandEmpty>}
            {ready && caseItems.length > 0 && (
              <CommandGroup heading="Expedientes">
                {caseItems.map((c) => (
                  <CommandItem key={c.id} value={`caso-${c.id}`} onSelect={() => go(`/pqrs/${c.id}`)}>
                    <span className="text-xs text-muted-foreground">{c.radication}</span><span className="flex-1 truncate">{c.subject}</span><StatusTag status={c.status} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {ready && people.length > 0 && (
              <CommandGroup heading="Personas">
                {people.map((p) => (
                  <CommandItem key={p.id} value={`persona-${p.id}`} onSelect={() => go(`/directorio/${p.id}`)}>
                    <span className="flex-1">{p.fullName}</span><span className="text-xs text-muted-foreground">{p.identificationNumber ?? p.email}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
